import './FlyOut.css'

import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  type Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStatus,
} from '@floating-ui/react'
import { clsx } from 'clsx'
import { cloneElement, type ReactElement, type ReactNode, type Ref } from 'react'

import { Button, type ButtonLabelProps } from '#design-system/component/Button/Button'
import { getCssToken, parseCssLengthPx } from '#design-system/cssTokens'

/** Button-config trigger (default). If used, flyout triggers from a button.
 * `actionRole` is narrowed to the two roles that make sense for "opens a floating panel" —
 * primary / destruction / navigation would mis-signal intent. */
type FlyOutButtonTrigger = ButtonLabelProps & {
  actionRole: 'secondary' | 'ghost'
  /** Presence implies disabled. Button internally wraps itself in <Tooltip title={disabledMessage}>.
   * FlyOut additionally short-circuits useClick so the panel can't open. */
  disabledMessage?: string
  /** Discriminant against the custom-element variant. */
  element?: never
}

/** Custom-element trigger — escape hatch for non-Button triggers
 * (Tenant Switcher, Date Picker input field, anywhere the standard Button shape doesn't fit).
 * FlyOut clones the element to inject the reference ref + click/aria reference props
 * from Floating UI's useInteractions. The element must accept a ref and onClick. */
interface FlyOutCustomTrigger {
  element: ReactElement
  actionRole?: never
  disabledMessage?: never
  label?: never
  icon?: never
  'aria-label'?: never
}

export type FlyOutTrigger = FlyOutButtonTrigger | FlyOutCustomTrigger

export interface FlyOutProps {
  /** Trigger config — Button-shaped or custom element. */
  trigger: FlyOutTrigger
  /** Panel content. Any JSX. FlyOut imposes no structural slots.
   * Consumers compose their own layout (MenuList, Section, raw markup). */
  children: ReactNode
  /** ARIA role on the panel.
   * Drives `aria-haspopup` on the trigger via useRole, and the panel's `role` attribute.
   * Narrowed from useRole's wider set to the three values FlyOut consumers actually need. */
  role: 'menu' | 'dialog' | 'listbox'
  /** Controlled open state. The consumer's setter is reused inside the panel
   * to dismiss after actions (e.g. menu item click). */
  isOpen: boolean
  /** Called when FlyOut wants to change open state — trigger click, Escape, outside click.
   * Consumer updates its `isOpen` source-of-truth. */
  onOpenChange: (isOpen: boolean) => void
  /** Preferred placement. Auto-flips on overflow via Floating UI's flip middleware.
   * Default 'bottom-start'. */
  placement?: Placement
  /** Accessible name for the panel itself (read alongside the trigger's name in dialog role).
   * Optional for menu/listbox where the trigger label usually suffices. */
  'aria-label'?: string
  /** Additional class for the panel element. */
  className?: string
}

interface ClonedElementProps {
  ref?: Ref<HTMLElement>
}

export const FlyOut = (props: FlyOutProps): ReactElement => {
  const { trigger, children, role, isOpen, onOpenChange, placement, className } = props
  const ariaLabel = props['aria-label']
  // `element in trigger` is the discriminator — its presence proves the custom-element variant.
  // The Button-trigger branch's `disabledMessage?: string` is optional,
  // so we still check whether it's set (truthy string).
  const isButtonDisabled = !('element' in trigger) && Boolean(trigger.disabledMessage)

  const {
    refs,
    floatingStyles,
    context,
    placement: resolvedPlacement,
  } = useFloating({
    open: isOpen,
    onOpenChange,
    placement: placement ?? 'bottom-start',
    // Position with top/left rather than a transform. A transformed ancestor becomes the containing
    // block for `position: fixed` descendants, which traps a Tooltip rendered inside the panel.
    transform: false,
    // Middleware derivables re-run on every position computation,
    // so token values are picked up live from CSS.
    // Density swaps and edits propagate without remounting the panel.
    middleware: [
      offset(() => parseCssLengthPx(getCssToken('--popover-offset'))),
      flip(() => ({ padding: parseCssLengthPx(getCssToken('--viewport-edge-padding')) })),
      shift(() => ({ padding: parseCssLengthPx(getCssToken('--viewport-edge-padding')) })),
    ],
    whileElementsMounted: autoUpdate,
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context, { enabled: !isButtonDisabled }),
    useDismiss(context),
    useRole(context, { role }),
  ])

  // Keep the panel mounted while the CSS exit animation plays.
  // `isMounted` is true from the moment isOpen flips to true until exit duration elapses after it flips back.
  // `status` is forwarded to the panel as data-status so CSS can target initial/open/close.
  const { isMounted, status } = useTransitionStatus(context, {
    duration: {
      open: +getCssToken('--floating-enter-duration'),
      close: +getCssToken('--floating-exit-duration'),
    },
  })

  // useMergeRefs is a hook, so call it unconditionally.
  // For the Button-trigger path the merged ref is unused (we pass refs.setReference directly),
  // so the extra call is cheap and harmless.
  const customElementOriginalRef =
    'element' in trigger
      ? ((trigger.element as ReactElement<ClonedElementProps>).props.ref ?? null)
      : null
  const mergedCustomElementRef = useMergeRefs([refs.setReference, customElementOriginalRef])
  const buttonReferenceRef = useMergeRefs([refs.setReference])
  const setAnchorRef = useMergeRefs([refs.setFloating])

  const triggerNode =
    'element' in trigger ? (
      cloneElement(trigger.element as ReactElement<ClonedElementProps>, {
        ref: mergedCustomElementRef,
        ...getReferenceProps(),
      })
    ) : (
      <Button {...trigger} ref={buttonReferenceRef} {...getReferenceProps()} />
    )

  return (
    <>
      {triggerNode}
      {/* arp-root: portaled out of the app container, so the panel must opt into base.css itself */}
      {isMounted ? (
        <FloatingPortal>
          <div ref={setAnchorRef} className="doss-flyout-anchor arp-root" style={floatingStyles}>
            <FloatingFocusManager context={context} modal={false}>
              <div
                aria-label={ariaLabel}
                className={clsx('doss-flyout', className)}
                data-placement={resolvedPlacement}
                data-status={status}
                {...getFloatingProps()}
              >
                {children}
              </div>
            </FloatingFocusManager>
          </div>
        </FloatingPortal>
      ) : null}
    </>
  )
}
